import { type ComponentPropsWithoutRef } from 'react';
import type { CategoryGroupInfoDto } from '@/api/gen/_model/categoryGroupInfoDto';
import { PublisherLabelMap, SchoolMapper } from '@/shared/category/util';
import { useSchoolInfoShortTranslation } from '@/shared/hooks/useSchoolInfoShortTranslation';
import { cn } from '@/utils/tailwind.util';
import type { SchoolType } from '@/views/build/type';
export const Chip = ({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'>) => {
  return <div className={cn('w-fit inline-flex px-xs py-3xs items-center justify-center text-body-sm-semibold text-neutral border border-alternative rounded-xs bg-default', className)} {...props} data-sentry-component="Chip" data-sentry-source-file="QuizInfoChip.tsx">
      {children}
    </div>;
};
export const PublisherChip = (props: {
  publisherId?: string;
  hideEtc?: boolean;
  className?: string;
}) => {
  if (!props.publisherId) return null;
  const PublisherLabel = PublisherLabelMap[props.publisherId];
  if (props.hideEtc && !PublisherLabel) {
    return null;
  }
  return <Chip className={props.className} data-sentry-element="Chip" data-sentry-component="PublisherChip" data-sentry-source-file="QuizInfoChip.tsx">
      {PublisherLabel || props.publisherId}
    </Chip>;
};
const DEFAULT_SCHOOL_CHIP_TONE = 'border-assistive bg-neutral';

/**
 * 학교급별 칩 색.
 *
 * 서버는 색을 내려주지 않는다 — 카테고리 요약(`QuizsetFolderSchoolSummary` 등)에 색 필드가
 * 없고, 화면 색은 전부 여기서 정한다. 이 맵이 **유일한 출처**이고 퀴즈 카드·폴더 칩·
 * 플레이 사이드바·퀴즈 목록 배지가 `SchoolInfoChip` 을 통해 공유한다.
 *
 * `Record<SchoolType, …>` 이라 **6종을 다 채워야 컴파일된다** — 색이 정해지지 않은 학교급도
 * 기본값으로 명시해 두어, 새 색을 넣을 때 한 줄만 바꾸면 되고 빠뜨리면 타입이 잡는다.
 */
const SCHOOL_CHIP_TONE: Record<SchoolType, string> = {
  primary: 'bg-green-50 border-opacity-assistive',
  middle: 'bg-blue-50 border-opacity-assistive',
  high: 'bg-pink-50 border-opacity-assistive',
  // 아래 셋은 아직 시안이 없어 기본 회색이다. 색이 정해지면 위와 같은 형태로 채운다.
  kindergarten: DEFAULT_SCHOOL_CHIP_TONE,
  college: DEFAULT_SCHOOL_CHIP_TONE,
  other: DEFAULT_SCHOOL_CHIP_TONE
};
export const SchoolInfoChip = ({
  className,
  schoolInfo,
  otherCount = 0
}: {
  className?: string;
  schoolInfo: Omit<CategoryGroupInfoDto, 'subject' | 'subSubject'>;
  otherCount?: number;
}) => {
  const {
    schoolType: type,
    schoolSemester,
    schoolGrade
  } = schoolInfo;
  const {
    schoolTypeText,
    schoolSemesterText,
    schoolGradeText
  } = useSchoolInfoShortTranslation({
    schoolInfo
  });
  const schoolType = SchoolMapper.getSchoolTypeNameById(Number(type));
  const isShowSchoolGrade = Number(schoolGrade) && schoolGrade;
  const joinText = `${schoolTypeText}${isShowSchoolGrade ? ` ${schoolGradeText}` : ''}${schoolSemester ? `-${schoolSemesterText}` : ''}`;
  return <Chip className={cn(SCHOOL_CHIP_TONE[schoolType], className)} data-sentry-element="Chip" data-sentry-component="SchoolInfoChip" data-sentry-source-file="QuizInfoChip.tsx">
      <span>{joinText}</span>
      {otherCount > 0 && <span className={'ml-4xs text-assistive'}>{`외 ${otherCount}`}</span>}
    </Chip>;
};